import { lazy, Suspense } from 'react';

import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';

import { ProtectedRoutes } from '@/components/ProtectedRoutes';
import { ConditionalProtectedRoutes } from '@/components/ConditionalProtectedRoutes';

import App from './App';
import SuspenseLoader from './components/SuspenseLoader';
import { RouteErrorElement } from './components/ErrorBoundary/RouteErrorElement';
import { Assets } from './pages/ClientConfiguration/Assets/Assets';
import { CampaignDetailsProvider } from './pages/ClientConfiguration/Campaign/CampaignDetailsContext';
import { CampaignDetailsTab } from './pages/ClientConfiguration/Campaign/CampaignDetailsTab';
import { Site } from './pages/ClientConfiguration/Site/Site';
import { SiteDetailsProvider } from './pages/ClientConfiguration/Site/SiteDetailsContext';
import { SiteDetailsTab } from './pages/ClientConfiguration/Site/SiteDetailsTab';
import { UsersProvider } from './pages/Users/UsersContext';

const GroupDetailsPage = lazy(async () => {
  const [{ GroupDetailsProvider }, { default: GroupDetails }] = await Promise.all([
    import('@/pages/Devices/GroupDetails/GroupDetailsContext'),
    import('@/pages/Devices/GroupDetails'),
  ]);

  return {
    default: () => (
      <GroupDetailsProvider>
        <GroupDetails />
      </GroupDetailsProvider>
    ),
  };
});

const DeviceDetailsPage = lazy(async () => {
  const [{ DeviceDetailsProvider }, { default: DeviceDetails }] = await Promise.all([
    import('@/pages/Devices/DeviceDetails/DeviceDetailsContext'),
    import('@/pages/Devices/DeviceDetails'),
  ]);

  return {
    default: () => (
      <DeviceDetailsProvider>
        <DeviceDetails />
      </DeviceDetailsProvider>
    ),
  };
});

// Pages
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const NoPermission = lazy(() => import('./pages/NoPermission'));
const NotFound = lazy(() => import('./pages/NotFound'));
const LoginPage = lazy(() => import('@/pages/Login/LoginPage'));
const ForgotPasswordPage = lazy(() => import('src/pages/Login/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/Login/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/Login/VerifyEmailPage'));
const ListsLanding = lazy(() => import('@/pages/Lists/ListsLanding'));
const ListDetailPage = lazy(() => import('@/pages/Lists/ListDetailPage'));
const ListDetailPeopleAdd = lazy(() => import('@/pages/Lists/ListDetailPeopleAdd'));
const PeoplePage = lazy(() => import('src/pages/People/List'));
const PeoplePageDetails = lazy(() => import('src/pages/People/Details'));
const PeopleDetailsEdit = lazy(() => import('src/pages/People/Edit'));
const Devices = lazy(() => import('src/pages/Devices/Devices'));
const DeviceNew = lazy(() => import('src/pages/Devices/DeviceNew'));
const Groups = lazy(() => import('src/pages/Devices/Groups'));
const GroupDetailsTab = lazy(() => import('src/pages/Devices/GroupDetails/GroupDetailsTab'));
const PeopleNew = lazy(() => import('src/pages/People/Edit'));
const AccessLogs = lazy(() => import('src/pages/AccessLogs/AccessLogs'));
const PaymentLogs = lazy(() => import('src/pages/PaymentLogs/PaymentLogs'));
const DeviceDetailsTab = lazy(() => import('src/pages/Devices/DeviceDetails/DeviceDetailsTab'));
const DeviceLogs = lazy(() => import('src/pages/Devices/DeviceLogs'));
const TicketLogs = lazy(() => import('src/pages/TicketLogs/TicketLogs'));
const AddNewGroup = lazy(() => import('src/pages/Devices/AddNewGroup'));
const Notifications = lazy(() => import('src/pages/Notifications'));
const Sites = lazy(() => import('src/pages/ClientConfiguration/Sites'));
const CreateSite = lazy(() => import('src/pages/ClientConfiguration/CreateSite'));
const BulkImport = lazy(() => import('src/pages/People/BulkImport'));
const ImportProcess = lazy(() => import('src/pages/People/ImportProcess'));
const Users = lazy(() => import('src/pages/Users'));
const Campaign = lazy(() => import('src/pages/ClientConfiguration/Campaign'));
const CampaignNew = lazy(() => import('src/pages/ClientConfiguration/Campaign/new'));
const UsersNew = lazy(() => import('src/pages/Users/UsersNew'));
const Connections = lazy(() => import('src/pages/ClientConfiguration/Connections'));
const ConnectionCreateAndEdit = lazy(() => import('src/pages/ClientConfiguration/Connections/ConnectionCreateAndEdit'));
const AccountSettings = lazy(() => import('src/pages/AccountSettings'));
const Kiosk = lazy(() => import('src/pages/ClientConfiguration/Kiosk'));
const TicketGroups = lazy(() => import('src/pages/ClientConfiguration/TicketGroups'));
const TicketGroupsNew = lazy(() => import('src/pages/ClientConfiguration/TicketGroups/pages/TicketGroupsNew'));
const TicketGroupsEdit = lazy(() => import('src/pages/ClientConfiguration/TicketGroups/pages/TicketGroupsEdit'));
const EmailTemplates = lazy(() => import('src/pages/ClientConfiguration/EmailTemplates'));
const NewEmailTemplate = lazy(() => import('src/pages/ClientConfiguration/EmailTemplates/EmailTemplateCreateAndEdit'));
const VenuesAndEvents = lazy(() => import('src/pages/VenuesAndEvents'));
const VenuesTab = lazy(() => import('src/pages/VenuesAndEvents/VenuesTab/VenuesTab'));
const VenueCreateAndEdit = lazy(() => import('src/pages/VenuesAndEvents/VenuesTab/VenueCreateAndEdit'));
const VenueDetails = lazy(() => import('src/pages/VenuesAndEvents/VenuesTab/VenueDetails'));
const DayPasses = lazy(() => import('src/pages/ClientConfiguration/DayPasses'));
const DayPassCreateAndEdit = lazy(() => import('src/pages/ClientConfiguration/DayPasses/DayPassCreateAndEdit'));
const Reports = lazy(() => import('src/pages/Reports/Reports'));

const renderSuspensedComponent = (Component: React.ComponentType) => (
  <Suspense fallback={<SuspenseLoader />}>
    <Component />
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: <ProtectedRoutes>{renderSuspensedComponent(App)}</ProtectedRoutes>,
    errorElement: <RouteErrorElement />,
    children: [
      {
        index: true,
        element: renderSuspensedComponent(Dashboard),
      },
      {
        element: <ProtectedRoutes />,
        children: [
          {
            path: 'reports',
            element: <ProtectedRoutes>{renderSuspensedComponent(Reports)}</ProtectedRoutes>,
          },
          {
            path: 'users',
            element: (
              <ProtectedRoutes requiredPermissions={['user:manager']}>
                <UsersProvider>{renderSuspensedComponent(Users)}</UsersProvider>
              </ProtectedRoutes>
            ),
          },
          {
            path: 'users/new',
            element: (
              <ProtectedRoutes requiredPermissions={['user:manager']}>
                {renderSuspensedComponent(UsersNew)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'users/:username',
            element: (
              <ProtectedRoutes requiredPermissions={['user:manager']}>
                {renderSuspensedComponent(UsersNew)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'notifications',
            element: (
              <ProtectedRoutes requiredPermissions={['alert:reader', 'alert:manager']}>
                {renderSuspensedComponent(Notifications)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'ticket-logs',
            element: (
              <ProtectedRoutes requiredPermissions={['alert:reader', 'alert:manager']}>
                {renderSuspensedComponent(TicketLogs)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'payment-logs',
            element: (
              <ProtectedRoutes requiredPermissions={['alert:reader', 'alert:manager']}>
                {renderSuspensedComponent(PaymentLogs)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'access-logs',
            element: (
              <ProtectedRoutes requiredPermissions={['alert:reader', 'alert:manager']}>
                {renderSuspensedComponent(AccessLogs)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'lists',
            element: (
              <ProtectedRoutes requiredPermissions={['poi:reader', 'poi:writer', 'poi:manager']}>
                {renderSuspensedComponent(ListsLanding)}
              </ProtectedRoutes>
            ),
            children: [
              {
                path: 'new',
                element: (
                  <ProtectedRoutes requiredPermissions={['poi:writer', 'poi:manager']}>
                    {renderSuspensedComponent(ListDetailPage)}
                  </ProtectedRoutes>
                ),
              },
              {
                path: ':id',
                element: (
                  <ProtectedRoutes requiredPermissions={['poi:reader', 'poi:writer', 'poi:manager']}>
                    {renderSuspensedComponent(ListDetailPage)}
                  </ProtectedRoutes>
                ),
              },
              {
                path: ':id/add-people',
                element: (
                  <ProtectedRoutes requiredPermissions={['poi:writer', 'poi:manager']}>
                    {renderSuspensedComponent(ListDetailPeopleAdd)}
                  </ProtectedRoutes>
                ),
              },
            ],
          },
          {
            path: 'people',
            element: (
              <ProtectedRoutes requiredPermissions={['poi:reader', 'poi:writer', 'poi:manager']}>
                {renderSuspensedComponent(PeoplePage)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'people/new',
            element: (
              <ProtectedRoutes requiredPermissions={['poi:writer', 'poi:manager']}>
                {renderSuspensedComponent(PeopleNew)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'people/:id/edit',
            element: (
              <ProtectedRoutes requiredPermissions={['poi:writer', 'poi:manager']}>
                {renderSuspensedComponent(PeopleDetailsEdit)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'people/:id',
            element: (
              <ProtectedRoutes requiredPermissions={['poi:reader', 'poi:writer', 'poi:manager']}>
                {renderSuspensedComponent(PeoplePageDetails)}
              </ProtectedRoutes>
            ),
            children: [
              {
                path: '',
                index: true,
                element: <Navigate to="information" replace />,
              },
              {
                path: ':tab',
                element: renderSuspensedComponent(PeoplePageDetails),
              },
            ],
          },
          {
            path: 'people/bulk-import',
            element: (
              <ProtectedRoutes requiredPermissions={['poi:writer', 'poi:manager']}>
                {renderSuspensedComponent(BulkImport)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'people/import-process/:jobId',
            element: (
              <ProtectedRoutes requiredPermissions={['poi:writer', 'poi:manager']}>
                {renderSuspensedComponent(ImportProcess)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'venues-and-events/:tab',
            element: (
              <ProtectedRoutes requiredPermissions={['venue:reader', 'venue:writer', 'venue:manager']}>
                {renderSuspensedComponent(VenuesAndEvents)}
              </ProtectedRoutes>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="venues" replace />,
              },
              {
                path: 'venues',
                children: [
                  {
                    index: true,
                    element: renderSuspensedComponent(VenuesTab),
                  },
                  {
                    path: ':id',
                    element: renderSuspensedComponent(VenueDetails),
                  },
                ],
              },
            ],
          },
          {
            path: 'venues-and-events/venues/new',
            element: (
              <ProtectedRoutes requiredPermissions={['venue:writer', 'venue:manager']}>
                {renderSuspensedComponent(VenueCreateAndEdit)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'venues-and-events/venues/:id/edit',
            element: (
              <ProtectedRoutes requiredPermissions={['venue:writer', 'venue:manager']}>
                {renderSuspensedComponent(VenueCreateAndEdit)}
              </ProtectedRoutes>
            ),
          },
          {
            path: '',
            element: <Navigate to="sensors-controllers" />,
          },
          {
            path: 'sensors-controllers',
            element: (
              <ProtectedRoutes requiredPermissions={['sensor:reader', 'sensor:writer', 'sensor:manager']}>
                {renderSuspensedComponent(Devices)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'sensors-controllers/new',
            element: (
              <ProtectedRoutes requiredPermissions={['sensor:manager']}>
                {renderSuspensedComponent(DeviceNew)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'sensors-controllers/:type/:id',
            element: (
              <ProtectedRoutes requiredPermissions={['sensor:reader', 'sensor:writer', 'sensor:manager']}>
                {renderSuspensedComponent(DeviceDetailsPage)}
              </ProtectedRoutes>
            ),
            children: [
              {
                path: '',
                index: true,
                element: renderSuspensedComponent(() => <Navigate to="status" />),
              },
              {
                path: ':tab',
                element: (
                  <ConditionalProtectedRoutes
                    requiredPermissions={['sensor:writer', 'sensor:manager']}
                    unprotectedTabs={['status']}
                  >
                    {renderSuspensedComponent(DeviceDetailsTab)}
                  </ConditionalProtectedRoutes>
                ),
              },
              {
                path: 'logs',
                element: (
                  <ProtectedRoutes requiredPermissions={['sensor:writer', 'sensor:manager']}>
                    {renderSuspensedComponent(DeviceLogs)}
                  </ProtectedRoutes>
                ),
              },
            ],
          },
          {
            path: 'groups',
            children: [
              {
                path: '',
                index: true,
                element: (
                  <ProtectedRoutes requiredPermissions={['sensor:reader', 'sensor:writer', 'sensor:manager']}>
                    {renderSuspensedComponent(Groups)}
                  </ProtectedRoutes>
                ),
              },
              {
                path: 'new',
                element: (
                  <ProtectedRoutes requiredPermissions={['sensor:writer', 'sensor:manager']}>
                    {renderSuspensedComponent(AddNewGroup)}
                  </ProtectedRoutes>
                ),
              },
              {
                path: ':id',
                element: (
                  <ProtectedRoutes requiredPermissions={['sensor:writer', 'sensor:manager']}>
                    {renderSuspensedComponent(GroupDetailsPage)}
                  </ProtectedRoutes>
                ),
                children: [
                  {
                    path: '',
                    index: true,
                    element: <Navigate to="settings" replace />,
                  },
                  {
                    path: ':tab',
                    element: renderSuspensedComponent(GroupDetailsTab),
                  },
                ],
              },
            ],
          },
          {
            path: 'connections',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(Connections)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'connections/new',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(ConnectionCreateAndEdit)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'connections/:id',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(ConnectionCreateAndEdit)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'kiosk/:tab?',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(Kiosk)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'sites',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(Sites)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'sites/new',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(CreateSite)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'sites/:id',
            element: renderSuspensedComponent(() => (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                <SiteDetailsProvider>
                  <Site />
                </SiteDetailsProvider>
              </ProtectedRoutes>
            )),
            children: [
              {
                path: '',
                index: true,
                element: <Navigate to="settings" replace />,
              },
              {
                path: ':tab',
                element: renderSuspensedComponent(SiteDetailsTab),
              },
            ],
          },
          {
            path: 'sites/:id/campaigns/new',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(CampaignNew)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'sites/:id/campaigns/:campaignId',
            element: renderSuspensedComponent(() => (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                <CampaignDetailsProvider>
                  <Campaign />
                </CampaignDetailsProvider>
              </ProtectedRoutes>
            )),
            children: [
              {
                path: '',
                index: true,
                element: <Navigate to="settings" replace />,
              },
              {
                path: ':tab',
                element: renderSuspensedComponent(CampaignDetailsTab),
              },
            ],
          },
          {
            path: 'email-templates',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(EmailTemplates)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'email-templates/new',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(NewEmailTemplate)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'email-templates/:id',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(NewEmailTemplate)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'day-passes',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(DayPasses)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'day-passes/new',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(DayPassCreateAndEdit)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'day-passes/:id',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(DayPassCreateAndEdit)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'ticket-groups',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(TicketGroups)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'ticket-groups/new',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(TicketGroupsNew)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'ticket-groups/:id/edit',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(TicketGroupsEdit)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'assets',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(Assets)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'account-settings',
            element: (
              <ProtectedRoutes requiredPermissions={['client:manager']}>
                {renderSuspensedComponent(AccountSettings)}
              </ProtectedRoutes>
            ),
          },
          {
            path: 'no-permission',
            element: renderSuspensedComponent(NoPermission),
          },
          {
            path: '*',
            element: renderSuspensedComponent(NotFound),
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: renderSuspensedComponent(LoginPage),
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/forgot-password',
    element: renderSuspensedComponent(ForgotPasswordPage),
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/reset-password/:token?',
    element: renderSuspensedComponent(ResetPasswordPage),
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/verify-email',
    element: renderSuspensedComponent(VerifyEmailPage),
    errorElement: <RouteErrorElement />,
  },
];

const router = createBrowserRouter(routes, {
  future: {
    // @ts-expect-error - future prop is not yet in the types i guess but is needed to not display warnings
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  },
});

export { router, routes };
